import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, Customer, CustomerAddress } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { Observable, filter, take } from 'rxjs';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { AddCustomerAddressComponent } from './add-customer-address/add-customer-address.component';
import { EditCustomerAddressComponent } from './edit-customer-address/edit-customer-address.component';
import { UpdateCustomerFeesComponent } from './update-customer-fees/update-customer-fees.component';

@Component({
  selector: 'app-customers',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, PaginationComponent, AddCustomerAddressComponent, EditCustomerAddressComponent, UpdateCustomerFeesComponent],
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.css'
})
export class CustomersComponent implements OnInit {
  customers$: Observable<{ items: Customer[], totalCount: number, pageIndex: number, pageSize: number }> | undefined;
  expandedRows: Set<string> = new Set();
  showAddAddressModal = false;
  selectedCustomerForAddress: Customer | null = null;
  showEditAddressModal = false;
  selectedAddress: CustomerAddress | null = null;
  showUpdateFeesModal = false;
  selectedCustomerForFees: Customer | null = null;
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount: number = 0;
  totalPages: number = 1;
  
  // Filter properties
  filters = {
    name: '',
    phoneNumber: '',
    hasUpiId: '',
    addressText: '',
    minCollections: '',
    minCollectionAmount: '',
    lastCollectionBefore: '',
    lastCollectionAfter: '',
    segment: 'all'
  };
  
  // Make Math available in template
  Math = Math;

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading customers
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadCustomers();
      });
  }

  loadCustomers(): void {
    // Convert page number to page index (page index is 0-based)
    const pageIndex = this.currentPage - 1;
    
    // Prepare filters object, only including non-empty values
    const activeFilters: any = {};
    if (this.filters.name.trim()) {
      activeFilters.name = this.filters.name.trim();
    }
    if (this.filters.phoneNumber.trim()) {
      activeFilters.phoneNumber = this.filters.phoneNumber.trim();
    }
    if (this.filters.addressText.trim()) {
      activeFilters.addressText = this.filters.addressText.trim();
    }
    if (this.filters.hasUpiId !== '') {
      activeFilters.hasUpiId = this.filters.hasUpiId === 'true';
    }
    if (this.filters.minCollections !== '') {
      activeFilters.minCollections = Number(this.filters.minCollections);
    }
    if (this.filters.minCollectionAmount !== '') {
      activeFilters.minCollectionAmount = Number(this.filters.minCollectionAmount);
    }
    if (this.filters.lastCollectionBefore) {
      activeFilters.lastCollectionBefore = this.filters.lastCollectionBefore;
    }
    if (this.filters.lastCollectionAfter) {
      activeFilters.lastCollectionAfter = this.filters.lastCollectionAfter;
    }
    
    this.customers$ = this.externalDataService.getCustomers(pageIndex, this.pageSize, activeFilters);
    this.customers$.subscribe(response => {
      if (response) {
        this.totalCount = response.totalCount || 0;
        this.calculateTotalPages();
      } else {
        this.totalCount = 0;
        this.totalPages = 1;
      }
    });
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.expandedRows.clear(); // Clear expanded rows when changing pages
    this.loadCustomers();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.expandedRows.clear(); // Clear expanded rows when changing page size
    this.loadCustomers();
  }

  toggleRow(customerId: string): void {
    if (this.expandedRows.has(customerId)) {
      this.expandedRows.delete(customerId);
    } else {
      this.expandedRows.add(customerId);
    }
  }

  isRowExpanded(customerId: string): boolean {
    return this.expandedRows.has(customerId);
  }

  getPrimaryAddress(customer: Customer): CustomerAddress | null {
    if (!customer.customerAddress || customer.customerAddress.length === 0) {
      return null;
    }
    return customer.customerAddress.find(addr => addr.isPrimary) || customer.customerAddress[0] || null;
  }

  openAppointmentModal(customer: Customer): void {
    this.router.navigate(['/appointments-list/create', customer.customerId]);
  }

  openAddAddressModal(customer: Customer): void {
    this.selectedCustomerForAddress = customer;
    this.showAddAddressModal = true;
  }

  closeAddAddressModal(): void {
    this.showAddAddressModal = false;
    this.selectedCustomerForAddress = null;
  }

  onAddressCreated(): void {
    this.closeAddAddressModal();
    this.loadCustomers(); // Refresh the customers list to show the new address
  }

  openEditAddressModal(address: CustomerAddress): void {
    this.selectedAddress = address;
    this.showEditAddressModal = true;
  }

  closeEditAddressModal(): void {
    this.showEditAddressModal = false;
    this.selectedAddress = null;
  }

  onAddressUpdated(): void {
    this.closeEditAddressModal();
    this.loadCustomers(); // Refresh the customers list to show the updated address
  }

  markAddressAsPrimary(addressId: string, customerId: string): void {
    if (confirm('Are you sure you want to mark this address as primary?')) {
      this.externalDataService.markAddressAsPrimary(addressId, customerId).subscribe({
        next: () => {
          // Refresh the customers list to reflect the change
          this.loadCustomers();
        },
        error: (error) => {
          console.error('Error marking address as primary:', error);
          alert('Failed to mark address as primary. Please try again.');
        }
      });
    }
  }

  deleteAddress(addressId: string, customerId: string): void {
    if (confirm('Are you sure you want to delete this address? This action cannot be undone.')) {
      this.externalDataService.deleteAddress(addressId, customerId).subscribe({
        next: () => {
          // Refresh the customers list to reflect the change
          this.loadCustomers();
        },
        error: (error) => {
          console.error('Error deleting address:', error);
          alert('Failed to delete address. Please try again.');
        }
      });
    }
  }

  // Filter methods
  onFilterChange(): void {
    // Optional: Implement debounced search or real-time filtering
    // For now, we'll just enable/disable the apply button
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.expandedRows.clear(); // Clear expanded rows when filtering
    this.loadCustomers();
  }

  clearFilters(): void {
    this.filters = {
      name: '',
      phoneNumber: '',
      hasUpiId: '',
      addressText: '',
      minCollections: '',
      minCollectionAmount: '',
      lastCollectionBefore: '',
      lastCollectionAfter: '',
      segment: 'all'
    };
    this.currentPage = 1; // Reset to first page when clearing filters
    this.expandedRows.clear(); // Clear expanded rows when clearing filters
    this.loadCustomers();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.name.trim() || 
              this.filters.phoneNumber.trim() || 
              this.filters.addressText.trim() || 
              this.filters.hasUpiId !== '' ||
              this.filters.minCollections !== '' ||
              this.filters.minCollectionAmount !== '' ||
              this.filters.lastCollectionBefore ||
              this.filters.lastCollectionAfter ||
              this.filters.segment !== 'all');
  }

  selectSegment(segment: string): void {
    this.filters.segment = segment;
    this.currentPage = 1;
    this.expandedRows.clear();
    
    // Reset collection-specific filters
    this.filters.minCollections = '';
    this.filters.minCollectionAmount = '';
    this.filters.lastCollectionBefore = '';
    this.filters.lastCollectionAfter = '';

    const today = new Date();

    if (segment === 'active_regulars') {
      this.filters.minCollections = '3'; // Regular is 3+ collections
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      this.filters.lastCollectionAfter = thirtyDaysAgo.toISOString().split('T')[0];
    } else if (segment === 'churned_regulars') {
      this.filters.minCollections = '3'; // Regular is 3+ collections
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(today.getDate() - 60);
      this.filters.lastCollectionBefore = sixtyDaysAgo.toISOString().split('T')[0];
    }
    
    this.loadCustomers();
  }

  openUpdateFeesModal(customer: Customer): void {
    this.selectedCustomerForFees = customer;
    this.showUpdateFeesModal = true;
  }

  closeUpdateFeesModal(): void {
    this.showUpdateFeesModal = false;
    this.selectedCustomerForFees = null;
  }

  onFeesUpdated(): void {
    this.closeUpdateFeesModal();
    this.loadCustomers(); // Refresh the customers list
  }

  // Delete Customer Logic
  deletingCustomerId: string | null = null;
  isDeletingCustomer: boolean = false;

  promptDeleteCustomer(customerId: string): void {
    this.deletingCustomerId = customerId;
  }

  cancelDeleteCustomer(): void {
    this.deletingCustomerId = null;
    this.isDeletingCustomer = false;
  }

  confirmDeleteCustomer(customer: Customer): void {
    if (!customer.phoneNumber) {
      alert('Cannot delete customer without a phone number.');
      this.cancelDeleteCustomer();
      return;
    }
    
    this.isDeletingCustomer = true;
    this.externalDataService.deleteCustomer(customer.phoneNumber).subscribe({
      next: () => {
        this.isDeletingCustomer = false;
        this.cancelDeleteCustomer();
        this.loadCustomers();
      },
      error: (error) => {
        console.error('Error deleting customer:', error);
        alert('Failed to delete customer.');
        this.isDeletingCustomer = false;
        this.cancelDeleteCustomer();
      }
    });
  }
} 